import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { User, Session } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  signUp: (
    email: string,
    password: string,
    displayName: string,
    captchaToken?: string | null,
  ) => Promise<{ error: string | null }>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (
    password: string,
  ) => Promise<{ error: string | null }>;
  updateDisplayName: (name: string) => Promise<{ error: string | null }>;
  updateEmail: (
    currentEmail: string,
    newEmail: string,
    password: string,
  ) => Promise<{ success: boolean; message: string }>;
  deleteAccount: () => Promise<{ error: string | null }>;
  /** Refresh the user object from Supabase (e.g. after metadata change) */
  refreshUser: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const REDIRECT_BASE =
  typeof window !== "undefined" ? window.location.origin : "https://bollyrhythm.com";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
  });

  // Bootstrap: get the current session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState({
        user: session?.user ?? null,
        session,
        loading: false,
      });
    });

    // Listen for auth state changes (login, logout, token refresh, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setState((prev) => ({
        ...prev,
        user: session?.user ?? null,
        session,
        loading: false,
      }));

      // When the user clicks the password-reset link from their email,
      // Supabase fires PASSWORD_RECOVERY — navigate them to the reset form.
      if (event === "PASSWORD_RECOVERY") {
        navigate("/app/auth/reset-password");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // --- Auth actions ---

  const signUp = useCallback(
    async (email: string, password: string, displayName: string, captchaToken?: string | null) => {
      const { data, error } = await supabase.functions.invoke("signup_with_captcha", {
        body: {
          email,
          password,
          displayName,
          captchaToken: captchaToken ?? undefined,
          emailRedirectTo: `${REDIRECT_BASE}/app/auth/confirmation`,
        },
      });
      if (error) return { error: error.message };
      if (data?.error) return { error: data.error };
      return { error: null };
    },
    [],
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error?.message ?? null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${REDIRECT_BASE}/app/auth/reset-password`,
    });
    return { error: error?.message ?? null };
  }, []);

  const updatePassword = useCallback(
    async (password: string) => {
      const { error } = await supabase.auth.updateUser({ password });
      return { error: error?.message ?? null };
    },
    [],
  );

  const updateDisplayName = useCallback(async (name: string) => {
    const { error } = await supabase.auth.updateUser({
      data: { display_name: name },
    });
    return { error: error?.message ?? null };
  }, []);

  const updateEmail = useCallback(
    async (currentEmail: string, newEmail: string, password: string) => {
      const { data, error } = await supabase.functions.invoke("email_update", {
        body: { currentEmail, newEmail, password },
      });
      if (error) {
        return { success: false, message: error.message };
      }
      return data as { success: boolean; message: string };
    },
    [],
  );

  const deleteAccount = useCallback(async () => {
    // Soft-delete: mark user metadata as deleted, then invoke cleanup function
    const { error: markError } = await supabase.auth.updateUser({
      data: { deleted: true },
    });
    if (markError) return { error: markError.message };

    const { error: fnError } = await supabase.functions.invoke(
      "delete_marked_users",
      { method: "POST" },
    );
    if (fnError) return { error: fnError.message };

    await supabase.auth.signOut();
    return { error: null };
  }, []);

  const refreshUser = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    setState((prev) => ({ ...prev, user }));
  }, []);

  // --- Memoised context value ---

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signUp,
      signIn,
      signOut,
      sendPasswordReset,
      updatePassword,
      updateDisplayName,
      updateEmail,
      deleteAccount,
      refreshUser,
    }),
    [
      state,
      signUp,
      signIn,
      signOut,
      sendPasswordReset,
      updatePassword,
      updateDisplayName,
      updateEmail,
      deleteAccount,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Full auth context (user, session, loading, and all auth actions) */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/** Convenience: just the user (or null). Throws if not within provider. */
export function useUser(): User | null {
  return useAuth().user;
}

/** Returns true while the initial session is being resolved. */
export function useAuthLoading(): boolean {
  return useAuth().loading;
}

/** Returns true if the current user has the admin role. */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.app_metadata?.role === "admin";
}
